import { useEffect, useState } from 'react';

const API = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:3000';

interface Resumo {
  assinaturas: { ativas: number; canceladas: number; spot: number; turbo: number };
  vendas7d: { actionTaken: string; n: number }[];
  whatsapp: { mensagens: number; contatos: number };
  pixPendente: { pendentes: number; pagos: number };
  recovery: { enviados: number; responderam: number };
}
interface Venda { receivedAt: string; actionTaken: string; cliente: string | null; produto: string | null; valor: string | null; parsedEmail: string | null; }
interface Conversa { phone: string; nome: string | null; mensagens: number; ultima: string; }

const LABELS: Record<string, string> = {
  user_created_and_subscription_active: 'Novas vendas ativadas',
  upgrade_spot_to_turbo: 'Upgrades Spot→Turbo',
  install_purchased_no_sub: 'Installs',
  pending_payment_registered: 'PIX gerados',
  subscription_canceled: 'Cancelamentos',
};
const brl = (c: string | null) => (c ? `R$ ${(Number(c) / 100).toFixed(2).replace('.', ',')}` : '—');
const hora = (s: string) => { try { return new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };

function Kpi({ titulo, valor, sub, cor }: { titulo: string; valor: string | number; sub?: string; cor: string }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
      <div className="text-[11px] uppercase tracking-widest text-slate-400">{titulo}</div>
      <div className={`mt-2 text-4xl font-bold tabular-nums ${cor}`}>{valor}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

export function OperacaoReal(): JSX.Element {
  const [d, setD] = useState<Resumo | null>(null);
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [err, setErr] = useState('');
  const [at, setAt] = useState('');

  useEffect(() => {
    const load = () => {
      fetch(`${API}/operacao/resumo`).then((r) => r.json()).then((j) => { setD(j); setAt(new Date().toLocaleTimeString('pt-BR')); setErr(''); }).catch((e) => setErr(String(e)));
      fetch(`${API}/operacao/vendas?limit=12`).then((r) => r.json()).then((j) => setVendas(j.vendas ?? [])).catch(() => {});
      fetch(`${API}/operacao/conversas?limit=12`).then((r) => r.json()).then((j) => setConversas(j.conversas ?? [])).catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
          NextBoot CRM — Operação Real
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Dados ao vivo do banco de produção {at && `· atualizado ${at}`} · atualiza a cada 15s
        </p>

        {err && <div className="mt-6 rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 text-rose-300">Erro ao carregar: {err}. O core está rodando em {API}?</div>}
        {!d && !err && <div className="mt-6 text-slate-400">Carregando operação real…</div>}

        {d && (
          <>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi titulo="Assinaturas ativas" valor={d.assinaturas.ativas} sub={`${d.assinaturas.canceladas} canceladas`} cor="text-emerald-400" />
              <Kpi titulo="Carteira" valor={`${d.assinaturas.spot}/${d.assinaturas.turbo}`} sub="Spot / Turbo" cor="text-cyan-400" />
              <Kpi titulo="WhatsApp" valor={d.whatsapp.mensagens.toLocaleString('pt-BR')} sub={`${d.whatsapp.contatos} contatos`} cor="text-blue-400" />
              <Kpi titulo="PIX pendente" valor={d.pixPendente.pendentes} sub={`${d.pixPendente.pagos} pagos · recuperável`} cor="text-amber-400" />
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-3">Eventos de venda — últimos 7 dias</div>
                <div className="space-y-2">
                  {d.vendas7d.map((v) => (
                    <div key={v.actionTaken} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300">{LABELS[v.actionTaken] ?? v.actionTaken}</span>
                      <span className="font-bold tabular-nums text-emerald-400">{v.n}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-3">Recuperação (Recovery)</div>
                <div className="flex items-end gap-6">
                  <div><div className="text-4xl font-bold text-cyan-400 tabular-nums">{d.recovery.enviados}</div><div className="text-xs text-slate-400 mt-1">disparos</div></div>
                  <div><div className="text-4xl font-bold text-emerald-400 tabular-nums">{d.recovery.responderam}</div><div className="text-xs text-slate-400 mt-1">responderam</div></div>
                  <div><div className="text-4xl font-bold text-amber-400 tabular-nums">{d.recovery.enviados ? Math.round((d.recovery.responderam / d.recovery.enviados) * 100) : 0}%</div><div className="text-xs text-slate-400 mt-1">taxa</div></div>
                </div>
                <div className="mt-4 text-xs text-slate-400 border-t border-slate-700 pt-3">{d.pixPendente.pendentes} PIX pendentes agora = oportunidade direta de recuperação.</div>
              </div>
            </div>

            {/* Últimas vendas + conversas reais */}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-3">Últimas vendas reais</div>
                <div className="divide-y divide-slate-700/60">
                  {vendas.map((v, i) => (
                    <div key={i} className="flex items-center justify-between py-2 text-sm">
                      <div className="min-w-0">
                        <div className="truncate text-slate-200">{v.cliente ?? v.parsedEmail ?? '—'}</div>
                        <div className="text-[11px] text-slate-500">{v.produto ?? '—'} · {hora(v.receivedAt)}</div>
                      </div>
                      <span className="font-bold tabular-nums text-emerald-400 ml-3">{brl(v.valor)}</span>
                    </div>
                  ))}
                  {vendas.length === 0 && <div className="text-xs text-slate-500 py-2">Sem vendas recentes.</div>}
                </div>
              </div>

              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-3">Conversas recentes (WhatsApp)</div>
                <div className="divide-y divide-slate-700/60">
                  {conversas.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-2 text-sm">
                      <div className="min-w-0">
                        <div className="truncate text-slate-200">{c.nome || c.phone}</div>
                        <div className="text-[11px] text-slate-500">{c.phone} · {hora(c.ultima)}</div>
                      </div>
                      <span className="text-[11px] text-slate-400 ml-3">{c.mensagens} msgs</span>
                    </div>
                  ))}
                  {conversas.length === 0 && <div className="text-xs text-slate-500 py-2">Sem conversas.</div>}
                </div>
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-slate-500">
              ✓ Lendo o banco de produção real via usuário dedicado <code className="text-slate-400">crm_app</code> (somente leitura, dados sensíveis protegidos).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
